import React, { useState, useRef, useEffect, useCallback } from 'react';

import Fentcoin from './components/Fentcoin';
import BouncingNod from './components/BouncingNod';
import LandingPage from './components/LandingPage';
import HeartRate from './components/HeartRate';
import MarqueeOutline from './components/MarqueeOutline';

import { Volume2, VolumeX, MessageCircle, Pill, Clapperboard, X, Twitter} from 'lucide-react';

function App() {
  const [isEntered, setIsEntered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [hoverText, setHoverText] = useState('');
  const audioRef = useRef(null);
  const audioRef2 = useRef(null);
  const videoRef = useRef(null);
  const contractAddress = "CGUDCQhjxRTQKU3MTxmkwQDtzDRy5rsgGunKGUYpump";

  const handleButtonHover = (text) => {
    setHoverText(text);
  };

  const handleButtonLeave = () => {
    setHoverText('');
  };

  const handleEnter = () => {
    if (audioRef.current) {
      audioRef.current.volume = 0.6;
      audioRef.current.play();
    }
    setIsEntered(true);
  };

  const handleVolumeToggle = () => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(contractAddress);
      if (audioRef2.current) {
        audioRef2.current.currentTime = 0;
        audioRef2.current.play().catch(error => {
          console.error('Error playing audio:', error);
        });
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleMenuClick = () => {
    if (audioRef2.current) {
      audioRef2.current.currentTime = 0;
      audioRef2.current.play().catch(error => {
        console.error('Error playing audio:', error);
      });
    }
  };

  const handleOpenVideo = () => {
    setIsVideoOpen(true);
    if (audioRef.current) {
      audioRef.current.pause();
    }

    if (audioRef2.current) {
      audioRef2.current.currentTime = 0;
      audioRef2.current.play().catch(error => {
        console.error('Error playing audio:', error);
      });
    }
  };

  const handleCloseVideo = useCallback(() => {
    setIsVideoOpen(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (audioRef.current && !isMuted) {
      audioRef.current.play();
    }
  }, [isMuted]);

  useEffect(() => {
    const currentVideo = videoRef.current;
    
    if (currentVideo) {
      const handleVideoEnd = () => {
        handleCloseVideo();
      };
      
      currentVideo.addEventListener('ended', handleVideoEnd);
      
      return () => {
        currentVideo.removeEventListener('ended', handleVideoEnd);
      };
    }
  }, [handleCloseVideo]);

  return (
    <>
      <audio ref={audioRef} loop>
        <source src="/bgm/Grade9.mp3" type="audio/mpeg" />
      </audio>
      <audio ref={audioRef2}>
        <source src="/bgm/fc.mp3" type="audio/mpeg" />
      </audio>

      {/* Video Modal */}
      {isVideoOpen && (
        <div className="fixed inset-0 bg-black/95 z-[1000] flex items-center justify-center">
          <button 
            onClick={handleCloseVideo}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
          <video
            ref={videoRef}
            className="max-w-[90vw] max-h-[90vh]"
            controls
            autoPlay
          >
            <source src="/video/fent.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      )}

      {!isEntered ? (
        <LandingPage onEnter={handleEnter} />
      ) : (
        <div className="relative min-h-screen bg-black flex items-center justify-center overflow-hidden">
          <MarqueeOutline />

          <div className="absolute top-[5%] right-[5%] z-[100]">
            <button 
              onClick={handleVolumeToggle}
              className="text-white/70 hover:text-white transition-colors cursor-pointer"
            >
              {isMuted ? 
                <VolumeX size={36} /> : 
                <Volume2 size={36} />
              }
            </button>
          </div>
          
          <div className="absolute top-[5%] left-1/2 -translate-x-1/2 z-[100] flex flex-col items-center gap-4">
            <div className="flex gap-4">
              <button 
                onMouseEnter={() => handleButtonHover('PUMP.FUN')}
                onMouseLeave={handleButtonLeave}
                onClick={() => window.open('https://pump.fun/coin/', '_blank', handleMenuClick())}
                className="text-red-700/70 hover:text-red-700 transition-colors cursor-pointer"
              >
                <Pill size={36} />
              </button>
              <button 
                onMouseEnter={() => handleButtonHover('TELEGRAM')}
                onMouseLeave={handleButtonLeave}
                onClick={() => window.open('https://t.me/fentcoinCTO', '_blank', handleMenuClick())}
                className="text-red-700/70 hover:text-red-700 transition-colors cursor-pointer"
              >
                <MessageCircle size={36} />
              </button>
              <button 
                onMouseEnter={() => handleButtonHover('X')}
                onMouseLeave={handleButtonLeave}
                onClick={() => window.open('https://x.com/fentcoin_sol', '_blank', handleMenuClick())}
                className="text-red-700/70 hover:text-red-700 transition-colors cursor-pointer"
              >
                <Twitter size={36} />
              </button>
              <button 
                onMouseEnter={() => handleButtonHover('CLICK ME')}
                onMouseLeave={handleButtonLeave}
                onClick={handleOpenVideo}
                className="text-red-700/70 hover:text-red-700 transition-colors cursor-pointer ml-6"
              >
                <Clapperboard size={36} />
              </button>
            </div>
            {hoverText && (
              <span className="text-white bold text-sm font-medium transition-opacity duration-200">
                {hoverText}
              </span>
            )}
          </div>

          <div className="relative w-full flex flex-col items-center">
            <div className="animate-drop-bounce">
              <Fentcoin />
            </div>
            <div className="flex justify-center w-full z-[10]">
              <span className="text-white text-lg">LEGENDARY GEEKERS ONLY.</span>
            </div>
            <div className="flex justify-center w-full z-[11]">
              <span className="text-white italic text-md">NOD WITH US...</span>
            </div>
            <HeartRate/>
            <div className="mt-12 z-[100]">
              <button
                onClick={handleCopyAddress}
                className="text-white/70 hover:text-white font-mono text-lg transition-colors cursor-pointer bg-neutral-900/50 px-4 py-2 rounded"
              >
                {copied ? "Copied." : `CA: ${contractAddress}`}
              </button>
            </div>
          </div>
          <div className="absolute inset-0 z-[9]">
            <BouncingNod />
          </div>
        </div>
      )}
    </>
  );
}

export default App;